import { ReactNode, useEffect } from 'react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { Button, Flex, HStack, Text, useMediaQuery } from '@chakra-ui/react'
import { MdEdit as EditIcon } from 'react-icons/md'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import SvgIcon from 'components/SvgIcon'
import MobileManageTab from 'components/MobileManageTab'
import { MOBILE_MEDIA_QUERY } from 'constants/mobile-page-template'
import MobileSubPageHeader, { MobileSubPageTag } from 'components/MobileSubPageHeader'
import PlayPageHeader from './PlayPageHeader'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import routes from 'routes'
import styles from './styles.module.scss'
import { EFeatureFlags } from 'constants/featureFlags'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'
import { getRoleIdFromMembership, isBasicUserByMembership, isOrgAdminByMembership } from 'utils/auth/role'

interface IHeaderBarProps {
  setIsManageMode?: (isManageMode: boolean) => void
  isManageMode?: boolean
  title: string | ReactNode
  // RallyPD dark surface (e.g. practice-detail). Defaults off so the shared
  // HowFactory routes keep their light gray header chrome.
  isDark?: boolean
  /**
   * The page's own name, shown alone on a phone. Together with `onBack` it marks
   * this page a sub page: `title`'s breadcrumb trail does not fit a 402px row, so
   * the phone gets a back arrow and this name instead. Dark routes only.
   */
  subPageTitle?: ReactNode
  /** Where the phone back arrow leads — the parent page, not browser history. */
  onBack?: () => void
  /** Status of the page (Draft, Archived …), shown beside the title on a phone. */
  subPageTags?: MobileSubPageTag[]
  /** The page's own actions, pinned to the right of the phone back bar. */
  mobileActions?: ReactNode
  controlBy?:
    | 'processDetail'
    | 'searchPage'
    | 'collectionPage'
    | 'collectionDetailPage'
    | 'archivePage'
    | 'processList'
    | 'groupMemberList'
    | 'userPage'
    | 'userDetailPage'
    | 'commonLibraryList'
    | 'plansPage'
    | 'workPlanDetail'
    | 'schedulePage'
    | 'scheduleAssignPage'
    | 'metricPage'
    | 'trainingAssignmentPage'
    | 'trainingAssignmentDetailPage'
}

const HeaderBar = ({
  setIsManageMode,
  isManageMode,
  title,
  controlBy,
  isDark,
  subPageTitle,
  onBack,
  subPageTags,
  mobileActions
}: IHeaderBarProps) => {
  const {
    processStore,
    searchStore,
    userStore,
    authStore,
    groupStore,
    commonLibraryStore,
    collectionStore,
    planStore,
    scheduleStore,
    metricStore
  } = useStores()
  const { trainingAssignmentStore } = useStores()
  const navigate = useNavigate()
  const [isMobile] = useMediaQuery(MOBILE_MEDIA_QUERY)
  const { isManagePermission } = collectionStore
  const { currentUserGroupMembers, userDetail } = userStore
  const { value: isCommonStepEnabled } = useFeatureFlagValue(EFeatureFlags.MODULAR_PROCESS, false)
  const currentRoleId = getRoleIdFromMembership(authStore.currentMembership)
  const isBasicUser = isBasicUserByMembership(authStore.currentMembership)
  const isEditor = currentUserGroupMembers?.some(
    groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR
  )
  const userCanManage = isEditor || !isBasicUser

  // Feature flag for practice plan detail manage mode
  const { value: isWorkPlanDetailManageModeEnabled } = useFeatureFlagValue(
    EFeatureFlags.WORK_PLAN_DETAIL_MANAGE_MODE,
    true
  )

  let showManageButton = false
  let isManageModeDefault = isManageMode

  if (!setIsManageMode && controlBy === 'processDetail') {
    isManageModeDefault = processStore?.isManageModeInDetail
    showManageButton = processStore?.canUserEditInProcessDetail
  }
  if (!setIsManageMode && controlBy === 'userDetailPage') {
    const canEditUserDetail: boolean =
      isOrgAdminByMembership(authStore.currentMembership) ||
      currentRoleId < (userDetail?.authRoleId ?? AuthRoleIdEnum.PLAYER)
    isManageModeDefault = userStore?.isManageModeInUserDetail
    showManageButton = userStore?.canUserEditInUserDetail && canEditUserDetail
  }
  if (!setIsManageMode && controlBy === 'processList') {
    isManageModeDefault = processStore?.isManageModeInProcessList
    showManageButton = processStore?.canUserEditInProcessList
  }
  if (!setIsManageMode && controlBy === 'searchPage') {
    isManageModeDefault = searchStore?.isManageMode
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'collectionPage') {
    isManageModeDefault = collectionStore?.isManageMode
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'collectionDetailPage') {
    isManageModeDefault = collectionStore?.isManageMode
    if (!isBasicUser) {
      // Coach/admin/owner manage any collection in their org, independent of
      // per-group editor membership. The group-editor gate below applies only
      // to basic users, who are scoped to groups where they hold edit rights.
      showManageButton = true
    } else if (isEditor) {
      showManageButton = isManagePermission
    } else {
      showManageButton = false
    }
  }
  if (!setIsManageMode && controlBy === 'archivePage') {
    isManageModeDefault = processStore?.isManageModeInArchive
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'groupMemberList') {
    isManageModeDefault = groupStore?.isManageModeInMemberList
    showManageButton = userCanManage
  }
  if (!setIsManageMode && controlBy === 'userPage') {
    isManageModeDefault = userStore?.isManageMode
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'commonLibraryList') {
    if (isBasicUser) {
      commonLibraryStore.isManageModeInList = false
    }
    isManageModeDefault = commonLibraryStore?.isManageModeInList
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'plansPage') {
    isManageModeDefault = planStore?.isManageModeInPlansList
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'schedulePage') {
    isManageModeDefault = scheduleStore?.isManageModeInScheduleList
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'scheduleAssignPage') {
    isManageModeDefault = scheduleStore?.isManageModeInAssignPage
    // Coach+ across teams (matches the schedule list and trainingAssignment pages).
    // Uses the cross-team max role, not the active-org membership, so it survives the
    // cross-team context where currentMembership is null (which would drop even an admin).
    showManageButton = authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH)
  }
  if (!setIsManageMode && controlBy === 'workPlanDetail') {
    isManageModeDefault = planStore?.isManageModeInDetail
    showManageButton = isWorkPlanDetailManageModeEnabled ? !isBasicUser : false
  }
  if (!setIsManageMode && controlBy === 'metricPage') {
    isManageModeDefault = metricStore?.isManageMode
    showManageButton = !isBasicUser
  }
  if (!setIsManageMode && controlBy === 'trainingAssignmentPage') {
    isManageModeDefault = trainingAssignmentStore?.isManageMode
    // Cross-team capability check — the active-org membership can be null (falls
    // back to PLAYER) and would wrongly hide Manage from a coach on this page.
    showManageButton = authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH)
  }
  if (!setIsManageMode && controlBy === 'trainingAssignmentDetailPage') {
    isManageModeDefault = trainingAssignmentStore?.isManageMode
    showManageButton = authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH)
  }

  useEffect(() => {
    if (isCommonStepEnabled) {
      commonLibraryStore.setFeatureEnabled(true)
    }
  }, [isCommonStepEnabled])

  const handleToggleManage = () => {
    if (controlBy === 'processDetail') {
      const next = !processStore?.isManageModeInDetail
      if (next) {
        const event = new CustomEvent('process_request_manage_enable', { detail: {} })
        window.dispatchEvent(event)
        return
      }
      processStore?.setManageModeInDetail(false)
    } else if (controlBy === 'processList') {
      processStore?.setManageModeInList(!processStore?.isManageModeInProcessList)
    } else if (controlBy === 'searchPage') {
      searchStore?.setManageMode(!searchStore?.isManageMode)
    } else if (controlBy === 'collectionPage') {
      collectionStore?.setManageMode(!collectionStore?.isManageMode)
    } else if (controlBy === 'collectionDetailPage') {
      collectionStore?.setManageMode(!collectionStore?.isManageMode, true)
    } else if (controlBy === 'archivePage') {
      processStore?.setManageModeInArchive(!processStore?.isManageModeInArchive)
    } else if (controlBy === 'groupMemberList') {
      groupStore?.setManageModeInMemberList(!groupStore?.isManageModeInMemberList)
    } else if (controlBy === 'userPage') {
      userStore?.setManageMode(!userStore?.isManageMode)
    } else if (controlBy === 'userDetailPage') {
      userStore?.setManageModeInUserDetail(!userStore?.isManageModeInUserDetail)
    } else if (controlBy === 'commonLibraryList') {
      commonLibraryStore?.setManageModeInList(!commonLibraryStore?.isManageModeInList)
    } else if (controlBy === 'plansPage') {
      planStore?.setManageModeInPlansList(!planStore?.isManageModeInPlansList)
    } else if (controlBy === 'schedulePage') {
      scheduleStore?.setManageModeInScheduleList(!scheduleStore?.isManageModeInScheduleList)
    } else if (controlBy === 'scheduleAssignPage') {
      scheduleStore?.setManageModeInAssignPage(!scheduleStore?.isManageModeInAssignPage)
    } else if (controlBy === 'workPlanDetail') {
      planStore?.setManageModeInDetail(!planStore?.isManageModeInDetail)
    } else if (controlBy === 'metricPage') {
      metricStore?.setManageMode(!metricStore?.isManageMode)
    } else if (controlBy === 'trainingAssignmentPage') {
      trainingAssignmentStore?.setManageMode(!trainingAssignmentStore?.isManageMode)
    } else if (controlBy === 'trainingAssignmentDetailPage') {
      trainingAssignmentStore?.setManageMode(!trainingAssignmentStore?.isManageMode)
    } else {
      setIsManageMode && setIsManageMode(!isManageMode)
    }
  }

  const canManage = Boolean(setIsManageMode || (controlBy && showManageButton))

  // The global mobile template, decided here so a light legacy route and a dark
  // RallyPD one wear the same chrome on a phone. A sub page trades its
  // breadcrumb row for the back bar — which stands in for the app topbar rather
  // than stacking under it — and Manage always becomes the edge tab, since
  // neither header has room for a labelled pill at this width.
  const manageTab = canManage ? (
    <MobileManageTab isManageMode={Boolean(isManageModeDefault)} onToggle={handleToggleManage} />
  ) : null

  // On a phone the breadcrumb row does not render at all. A main page is named by
  // the nav bars around it, so repeating the name costs a row of a short screen;
  // a sub page has no nav bars — the back bar below replaces them — so it is the
  // one that keeps a header.
  if (isMobile) {
    return (
      <>
        {subPageTitle && onBack && (
          <MobileSubPageHeader title={subPageTitle} onBack={onBack} tags={subPageTags} actions={mobileActions} />
        )}
        {manageTab}
      </>
    )
  }

  // Dark RallyPD chrome delegates to the shared PlayPageHeader so the
  // practice-detail header is literally the same component as the plans list.
  if (isDark) {
    return (
      <PlayPageHeader
        breadcrumb={
          typeof title === 'string' ? (
            <Text fontFamily="Poppins" color="#F2F7FA" fontWeight={600} fontSize="16px" noOfLines={1} m={0}>
              {title}
            </Text>
          ) : (
            title
          )
        }
        showManage={canManage}
        isManageActive={isManageModeDefault}
        onToggleManage={handleToggleManage}
      />
    )
  }

  return (
    <HStack background="gray.100" justifyContent="space-between" className={styles.headerContainer}>
      <HStack spacing={2} justifyContent="space-between">
        <HStack fontSize={{ base: 'sm', md: 'md' }} lineHeight={6} spacing={1}>
          <SvgIcon
            iconName="home"
            size={24}
            cursor="pointer"
            onClick={() => navigate(routes.dashboard.value, { replace: true })}
          />
          <ChevronRightIcon />
          {typeof title === 'string' ? (
            <Text fontFamily="Poppins" color="gray.700">
              {title}
            </Text>
          ) : (
            title
          )}
        </HStack>
      </HStack>
      {canManage && (
        <Button
          variant="outline"
          onClick={handleToggleManage}
          display={{ base: 'none', md: 'inline-flex' }}
          gap={{ base: 0, md: 2 }}
          padding={{ base: '10px', md: '16px' }}
          fontSize={{
            base: '0px',
            md: '16px'
          }}
          fontWeight={'500'}
          lineHeight={'24px'}
          color="gray.700"
          background="whiteAlpha.700"
          border={0}
          _hover={{
            background: 'gray.200'
          }}
          _active={{
            background: 'gray.300'
          }}
        >
          <Flex height={6} alignItems="center">
            {isManageModeDefault ? (
              <SvgIcon size={16} iconName="circle-check" color="#2D3748" />
            ) : (
              <EditIcon className={styles.icon} />
            )}
          </Flex>
          {isManageModeDefault ? 'Done' : 'Manage'}
        </Button>
      )}
      {manageTab}
    </HStack>
  )
}

export default observer(HeaderBar)
